import * as React from 'react'
import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateClient, type Client } from '@/features/admin/api'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import '../ManagePages.css'

interface Props {
  client: Client | null
  onClose: () => void
}

export function EditOrganizationDrawer({ client, onClose }: Props): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [orgName, setOrgName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')

  useEffect(() => {
    if (client) {
      setOrgName(client.name)
      setContactName(client.primary_contact_name ?? '')
      setContactEmail(client.primary_contact_email ?? '')
    }
  }, [client])

  const update = useMutation({
    mutationFn: () =>
      updateClient(client!.id, {
        org_name: orgName.trim() || undefined,
        primary_contact_name: contactName.trim() || undefined,
        primary_contact_email: contactEmail.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-orgs'] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
      toast.success('Organization updated', `${orgName} has been updated.`)
      onClose()
    },
    onError: (err) =>
      toast.error('Could not update', err instanceof ApiError ? err.message : 'Try again.'),
  })

  return (
    <Drawer
      open={client !== null}
      onClose={onClose}
      title="Edit organization"
      subtitle={client ? `Editing ${client.name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={update.isPending}>Cancel</Button>
          <Button onClick={() => update.mutate()} loading={update.isPending} disabled={!orgName.trim()}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="drawer-sections">
        <div className="drawer-section">
          <div className="drawer-section-label">Organization</div>
          <FormField label="Organization name" htmlFor="eo-name" required>
            <Input id="eo-name" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Sunrise Care Group" />
          </FormField>
        </div>

        <div className="drawer-section">
          <div className="drawer-section-label">Primary contact</div>
          <FormField label="Contact name" htmlFor="eo-cname">
            <Input id="eo-cname" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
          </FormField>
          <FormField label="Contact email" htmlFor="eo-cemail">
            <Input id="eo-cemail" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@org.com" />
          </FormField>
        </div>
      </div>
    </Drawer>
  )
}
